import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AdminLayout from '../../components/AdminLayout';
import api from '../../services/api';
import Swal from 'sweetalert2';
import { getReceiptUploadLabel } from '../../utils/receiptFormLabel';
import {
  Type,
  Mail,
  Phone,
  Calendar,
  ChevronsUpDown,
  CircleDot,
  SquareCheck,
  FileText,
  Video,
  User,
  Wrench,
  Settings,
  Eye,
  Paperclip,
  ArrowLeft,
  X,
} from 'lucide-react';

function Glyph({ icon: Icon, className = 'w-4 h-4 shrink-0' }) {
  if (!Icon) return null;
  return <Icon className={className} strokeWidth={2} />;
}

// Field Types Configuration
const FIELD_TYPES = [
  { 
    type: 'text', 
    label: 'Single Line Text', 
    icon: Type,
    description: 'For short text responses'
  },
  { 
    type: 'email', 
    label: 'Email', 
    icon: Mail,
    description: 'Email address with validation'
  },
  { 
    type: 'phone', 
    label: 'Phone Number', 
    icon: Phone,
    description: 'Phone number input'
  },
  { 
    type: 'date', 
    label: 'Date Input', 
    icon: Calendar,
    description: 'Date picker input'
  },
  { 
    type: 'dropdown', 
    label: 'Dropdown', 
    icon: ChevronsUpDown,
    description: 'Select from a list',
    hasOptions: true
  },
  { 
    type: 'radio', 
    label: 'Radio Buttons', 
    icon: CircleDot,
    description: 'Select one option',
    hasOptions: true
  },
  { 
    type: 'checkbox', 
    label: 'Checkboxes', 
    icon: SquareCheck,
    description: 'Select multiple options',
    hasOptions: true
  },
  { 
    type: 'textarea', 
    label: 'Paragraph Text', 
    icon: FileText,
    description: 'For longer text responses'
  },
  {
    type: 'video_recorder',
    label: 'Video Recorder',
    icon: Video,
    description: 'Record a video before submission'
  }
];

// Sortable Field Component
function SortableField({ field, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const fieldTypeConfig = FIELD_TYPES.find(t => t.type === field.field_type);
  const fieldType = fieldTypeConfig
    ? { icon: fieldTypeConfig.icon, label: fieldTypeConfig.label }
    : {
        icon: field.icon || Wrench,
        label: field.field_type === 'email'
          ? 'Email'
          : field.field_type === 'phone'
            ? 'Phone'
            : 'Text Input'
      };

  const cardClasses = [
    'border-2',
    'rounded-xl',
    'p-4',
    'mb-3',
    'transition-colors',
    isDragging ? 'border-teal-500 bg-white' : 'border-gray-200 bg-white'
  ];
  if (!isDragging && field.is_system) {
    cardClasses.push('bg-amber-50', 'border-amber-200');
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cardClasses.join(' ')}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start flex-1 min-w-0">
          <div
            {...attributes}
            {...listeners}
            className="cursor-move mr-3 mt-1 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
            </svg>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1 min-w-0">
              <Glyph icon={fieldType?.icon} className="w-5 h-5 shrink-0 text-gray-600" />
              <h4 className="font-semibold text-gray-900 break-words min-w-0">
                {field.field_label}
              </h4>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full shrink-0 ${
                field.is_required 
                  ? 'bg-red-100 text-red-700' 
                  : 'bg-gray-100 text-gray-700'
              }`}>
                {field.is_required ? 'Required' : 'Optional'}
              </span>
            </div>
            <p className="text-sm text-gray-500">
              {fieldType?.label} • Field name: {field.field_name}
            </p>
            {field.field_options?.options && (
              <p className="text-xs text-gray-400 mt-1">
                Options: {field.field_options.options.join(', ')}
              </p>
            )}
            {field.field_conditions?.visibility && (
              <p className="text-xs text-purple-600 mt-1">
                <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                {field.field_conditions.visibility.type === 'show_if' ? 'Shows' : 'Hides'} when {field.field_conditions.visibility.field} = {field.field_conditions.visibility.value}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center space-x-2 shrink-0">
          <button
            onClick={() => onEdit(field)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit field"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => onDelete(field.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title={field.is_system ? "Remove system field" : "Delete field"}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
          {field.is_system && (
            <span className="text-xs bg-gray-200 px-3 py-1 rounded-full uppercase tracking-wide text-gray-600">
              System
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function FormEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('builder');
  const [form, setForm] = useState(null);
  const [fields, setFields] = useState([]);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [fieldData, setFieldData] = useState({
    field_type: 'text',
    field_name: '',
    field_label: '',
    field_placeholder: '',
    is_required: false,
    options: [''],
    field_conditions: null,
    is_system: false,
    is_multiselect: false,
    enable_quantity_input: false
  });

  const fieldLabelInputRef = useRef(null);

  // Protect input from browser extension interference
  useEffect(() => {
    const input = fieldLabelInputRef.current;
    if (!input || !showFieldModal) return;

    // Ensure input has a form reference to prevent extension errors
    const ensureFormReference = () => {
      // The input should now have a form since we wrapped it in a form element
      // But ensure it's properly set
      let formElement = input.closest('form');
      if (formElement && !input.form) {
        // If form exists but input.form is not set, try to set it
        try {
          // The form property is read-only, so we can't set it directly
          // But the browser should handle this automatically
        } catch (e) {
          // Ignore
        }
      }
    };

    // Ensure all expected properties exist
    const ensureProperties = () => {
      ensureFormReference();
      
      // Ensure control property exists if extensions try to access it
      if (!input.control) {
        try {
          Object.defineProperty(input, 'control', {
            get: () => input, // Return the input itself as control
            configurable: true,
            enumerable: false
          });
        } catch (e) {
          // Ignore if we can't define it
        }
      }
    };

    // Run immediately
    ensureProperties();

    // Also ensure on focus/input events
    const handleEvent = () => {
      ensureProperties();
    };

    ['focus', 'click', 'input', 'keydown'].forEach(eventType => {
      input.addEventListener(eventType, handleEvent, { capture: true, passive: true });
    });

    // Use MutationObserver to catch any property access attempts
    const observer = new MutationObserver(() => {
      ensureProperties();
    });

    observer.observe(input, {
      attributes: true,
      attributeFilter: ['name', 'id', 'type']
    });

    return () => {
      ['focus', 'click', 'input', 'keydown'].forEach(eventType => {
        input.removeEventListener(eventType, handleEvent, { capture: true });
      });
      observer.disconnect();
    };
  }, [showFieldModal]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const activeFieldType = FIELD_TYPES.find(t => t.type === fieldData.field_type) || null;

  useEffect(() => {
    fetchForm();
  }, [id]);

  const fetchForm = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/forms/${id}`);
      const apiForm = response.data.form;

      const savedPositions = localStorage.getItem(`form_${id}_system_positions`);
      const systemPositions = savedPositions ? JSON.parse(savedPositions) : {};

      const savedConfigRaw = localStorage.getItem(`form_${id}_system_config`);
      const systemConfig = savedConfigRaw ? JSON.parse(savedConfigRaw) : {};

      let mergedSystemConfig = { ...(apiForm.system_fields_config || {}) };
      if (systemConfig.system_receipt && typeof systemConfig.system_receipt === 'object') {
        mergedSystemConfig.system_receipt = {
          ...(mergedSystemConfig.system_receipt || {}),
          ...systemConfig.system_receipt
        };
      }
      setForm({
        ...apiForm,
        system_fields_config: mergedSystemConfig
      });
      const systemFieldOverrides = mergedSystemConfig || {};
      
      // Get deleted system fields
      const deletedSystemFields = JSON.parse(localStorage.getItem(`form_${id}_deleted_system_fields`) || '[]');
      
      const buildSystemField = (fieldId, defaults) => {
        const overrides = systemFieldOverrides[fieldId] || {};
        const localOverrides = systemConfig[fieldId] || {};
        const merged = {
          ...defaults,
          ...overrides,
          ...localOverrides
        };
        if (overrides.hasOwnProperty('is_required')) {
          merged.is_required = !!overrides.is_required;
        }
        if (localOverrides.hasOwnProperty('is_required')) {
          merged.is_required = !!localOverrides.is_required;
        }
        merged.field_placeholder = localOverrides.field_placeholder ?? overrides.field_placeholder ?? defaults.field_placeholder ?? '';
        merged.field_label = localOverrides.field_label ?? overrides.field_label ?? defaults.field_label;
        merged.is_system = true;
        merged.icon = defaults.icon;
        merged.field_name = defaults.field_name;
        merged.field_type = defaults.field_type;
        merged.sort_order = defaults.sort_order;
        return merged;
      };

      // Define all available system fields
      const allSystemFieldsDefinitions = [
        {
          id: 'system_name',
          field_type: 'text',
          field_name: 'name',
          field_label: 'Full Name',
          is_required: true,
          icon: User,
          sort_order: systemPositions.system_name ?? 0,
          field_placeholder: ''
        },
        {
          id: 'system_email',
          field_type: 'email',
          field_name: 'email',
          field_label: 'Email Address',
          is_required: true,
          icon: Mail,
          sort_order: systemPositions.system_email ?? 1,
          field_placeholder: ''
        },
        {
          id: 'system_phone',
          field_type: 'phone',
          field_name: 'phone',
          field_label: 'Phone Number',
          is_required: false,
          icon: Phone,
          sort_order: systemPositions.system_phone ?? 2,
          field_placeholder: ''
        }
      ];

      // Build only non-deleted system fields
      const systemFields = allSystemFieldsDefinitions
        .filter(def => !deletedSystemFields.includes(def.id))
        .map(def => buildSystemField(def.id, def));
      
      const customFields = (response.data.fields || []).map(field => ({
        ...field,
        is_system: !!field.is_system
      }));
      const allFields = [...systemFields, ...customFields].sort((a, b) => a.sort_order - b.sort_order);
      setFields(allFields);
    } catch (err) {
      console.error('Fetch form error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load form',
        confirmButtonText: 'OK',
        zIndex: 10000
      }).then(() => {
        navigate('/admin/forms');
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = fields.findIndex(field => field.id === active.id);
      const newIndex = fields.findIndex(field => field.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newFields = arrayMove(fields, oldIndex, newIndex);
        
        // Update sort order for all fields
        const updatedFields = newFields.map((field, idx) => ({
          ...field,
          sort_order: idx
        }));
        
        setFields(updatedFields);

        // Save system field positions to localStorage
        const systemPositions = {};
        updatedFields.forEach(field => {
          if (field.is_system) {
            systemPositions[field.id] = field.sort_order;
          }
        });
        localStorage.setItem(`form_${id}_system_positions`, JSON.stringify(systemPositions));

        // Update backend
        try {
          // Update all fields (including system fields) in backend
          const fieldsToUpdate = updatedFields
            .map((field) => ({
              id: field.id,
              sort_order: field.sort_order
            }));
          
          await api.put(`/admin/forms/${id}/fields/reorder`, {
            fields: fieldsToUpdate
          });
        } catch (err) {
          console.error('Reorder fields error:', err);
        }
      }
    }
  };

  const handleAddField = (fieldType) => {
    setEditingField(null);
    setFieldData({
      field_type: fieldType.type,
      field_name: '',
      field_label: '',
      field_placeholder: '',
      is_required: false,
      options: [''],
      field_conditions: null,
      is_system: false
    });
    setShowFieldModal(true);
  };

  const handleEditField = (field) => {
    setEditingField(field);
    const fieldTypeConfig = FIELD_TYPES.find(t => t.type === field.field_type);
    let options = [''];
    if (fieldTypeConfig?.hasOptions) {
      if (field.field_options) {
        if (typeof field.field_options === 'string') {
          try {
            const parsed = JSON.parse(field.field_options);
            if (Array.isArray(parsed?.options)) {
              options = parsed.options;
            }
          } catch (e) {
            console.warn('Failed to parse field options for field:', field.id, e);
          }
        } else if (Array.isArray(field.field_options?.options)) {
          options = field.field_options.options;
        }
      }
    }

    setFieldData({
      field_type: field.field_type,
      field_name: field.field_name,
      field_label: field.field_label,
      field_placeholder: field.field_placeholder || '',
      is_required: field.is_required,
      options,
      field_conditions: field.field_conditions || null,
      is_system: !!field.is_system,
      is_multiselect: !!field.is_multiselect,
      enable_quantity_input: !!field.enable_quantity_input
    });
    setShowFieldModal(true);
  };

  const handleSaveField = async () => {
    const isSystemReceipt = editingField?.id === 'system_receipt';
    if (!fieldData.field_name || (!isSystemReceipt && !(fieldData.field_label || '').trim())) {
      Swal.fire({
        icon: 'warning',
        title: 'Required Fields',
        text: isSystemReceipt ? 'Field name is required' : 'Field name and label are required',
        confirmButtonText: 'OK',
        zIndex: 10000
      });
      return;
    }

    const isSystemField = editingField?.is_system || fieldData.is_system;
    if (isSystemField) {
      try {
        // For system fields, we'll try the API first, but if it fails due to missing column,
        // we'll fall back to localStorage-only updates
        try {
          if (isSystemReceipt) {
            await api.put(`/admin/forms/${id}/fields/system_receipt`, {
              field_label: (fieldData.field_label || '').trim()
            });
          } else {
            await api.put(`/admin/forms/${id}/fields/${editingField.id}`, {
              field_label: fieldData.field_label,
              field_placeholder: fieldData.field_placeholder ?? '',
              is_required: !!fieldData.is_required
            });
          }
        } catch (apiError) {
          console.warn('API update failed, falling back to localStorage:', apiError);
          // If API fails (likely due to missing system_fields_config column),
          // we'll just update localStorage and show success
        }
        
        // Always update localStorage for system fields
        const systemFieldOverrides = JSON.parse(localStorage.getItem(`form_${id}_system_config`) || '{}');
        systemFieldOverrides[editingField.id] = isSystemReceipt
          ? {
              ...systemFieldOverrides[editingField.id],
              field_label: (fieldData.field_label || '').trim()
            }
          : {
              ...systemFieldOverrides[editingField.id],
              field_label: fieldData.field_label,
              field_placeholder: fieldData.field_placeholder ?? '',
              is_required: !!fieldData.is_required
            };
        localStorage.setItem(`form_${id}_system_config`, JSON.stringify(systemFieldOverrides));
        
        // Show success toast
        setSuccessMessage(
          editingField?.id === 'system_receipt'
            ? 'Receipt label updated successfully!'
            : 'System field updated successfully!'
        );
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
        
        fetchForm();
        setShowFieldModal(false);
      } catch (err) {
        console.error('Save system field error:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to update system field',
          confirmButtonText: 'OK',
          zIndex: 10000
        });
      }
      return;
    }

    const fieldType = FIELD_TYPES.find(t => t.type === fieldData.field_type);
    // Calculate sort order considering all fields
    const maxSortOrder = Math.max(...fields.map(f => f.sort_order || 0), 0);
    
    const fieldPayload = {
      ...fieldData,
      field_options: fieldType?.hasOptions 
        ? { options: fieldData.options.filter(opt => opt.trim()) }
        : null,
      sort_order: editingField ? editingField.sort_order : maxSortOrder + 1,
      is_multiselect: fieldData.is_multiselect,
      enable_quantity_input: fieldData.enable_quantity_input
    };

    try {
      if (editingField) {
        await api.put(`/admin/forms/${id}/fields/${editingField.id}`, fieldPayload);
      } else {
        await api.post(`/admin/forms/${id}/fields`, fieldPayload);
      }
      fetchForm();
      setShowFieldModal(false);
    } catch (err) {
      console.error('Save field error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to save field',
        confirmButtonText: 'OK',
        zIndex: 10000
      });
    }
  };

  const handleDeleteField = async (fieldId) => {
    const field = fields.find(f => f.id === fieldId);
    const isSystemField = field?.is_system;

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: isSystemField 
        ? 'Are you sure you want to remove this system field? You can add it back later if needed.'
        : 'Are you sure you want to delete this field?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove it!',
      cancelButtonText: 'Cancel',
      zIndex: 10000
    });

    if (!result.isConfirmed) return;

    try {
      if (isSystemField) {
        // For system fields, just mark as deleted in localStorage
        const deletedSystemFields = JSON.parse(localStorage.getItem(`form_${id}_deleted_system_fields`) || '[]');
        if (!deletedSystemFields.includes(fieldId)) {
          deletedSystemFields.push(fieldId);
          localStorage.setItem(`form_${id}_deleted_system_fields`, JSON.stringify(deletedSystemFields));
        }
        fetchForm();
      } else {
        // For custom fields, delete from backend
        await api.delete(`/admin/forms/${id}/fields/${fieldId}`);
        fetchForm();
      }
    } catch (err) {
      console.error('Delete field error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to delete field',
        confirmButtonText: 'OK',
        zIndex: 10000
      });
    }
  };

  const handleRestoreSystemField = (fieldId) => {
    const deletedSystemFields = JSON.parse(localStorage.getItem(`form_${id}_deleted_system_fields`) || '[]');
    const updated = deletedSystemFields.filter(id => id !== fieldId);
    localStorage.setItem(`form_${id}_deleted_system_fields`, JSON.stringify(updated));
    fetchForm();
  };

  const handleEditReceiptLabel = () => {
    const sys = form?.system_fields_config;
    const stored =
      sys && typeof sys === 'object' && !Array.isArray(sys)
        ? sys.system_receipt?.field_label
        : '';
    setEditingField({
      id: 'system_receipt',
      is_system: true,
      field_type: 'file',
      field_name: 'receipt',
      field_label: typeof stored === 'string' ? stored : '',
      icon: Paperclip,
      sort_order: 999
    });
    setFieldData({
      field_type: 'file',
      field_name: 'receipt',
      field_label: typeof stored === 'string' ? stored : '',
      field_placeholder: '',
      is_required: true,
      options: [''],
      field_conditions: null,
      is_system: true,
      is_multiselect: false,
      enable_quantity_input: false
    });
    setShowFieldModal(true);
  };

  const handleUpdateForm = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/forms/${id}`, {
        name: form.name,
        description: form.description,
        form_headline: form.form_headline,
        submit_button_text: form.submit_button_text,
        success_message: form.success_message
      });
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Form settings updated successfully',
        confirmButtonText: 'OK',
        zIndex: 10000
      });
    } catch (err) {
      console.error('Update form error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update form settings',
        confirmButtonText: 'OK',
        zIndex: 10000
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout activeSection="FORMS">
        <div className="flex justify-center items-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      </AdminLayout>
    );
  }

  const isEditingSystemReceipt = editingField?.id === 'system_receipt';

  return (
    <AdminLayout activeSection="FORMS">
      <style>
        {`
          @keyframes fadeInRight {
            from {
              opacity: 0;
              transform: translateX(20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          .animate-fadeInRight {
            animation: fadeInRight 0.3s ease-out;
          }
        `}
      </style>

      {/* Success notification */}
      {showSuccess && (
        <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
          <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/admin/forms')}
            className="text-white hover:text-gray-200 mr-4"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">{form?.form_name || form?.name}</h1>
            <p className="text-white/70">Campaign{form?.campaign_names && form.campaign_names.includes(',') ? 's' : ''}: {form?.campaign_names || form?.campaign_name || 'Not assigned'}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-1 inline-flex mb-6">
        {[
          { id: 'builder', label: 'Form Builder', icon: Wrench },
          { id: 'settings', label: 'Form Settings', icon: Settings },
          { id: 'preview', label: 'Preview', icon: Eye }
        ].map((tab) => {
          const TabIcon = tab.icon;
          return (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center space-x-1.5 ${
              activeTab === tab.id
                ? 'bg-white text-teal-800 shadow-lg'
                : 'text-white hover:bg-white/10'
            }`}
          >
            <TabIcon className="w-4 h-4" />
            <span>{tab.label}</span>
          </button>
          );
        })}
      </div>

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {activeTab === 'builder' && (
          <>
            {/* Form Fields - Left Side */}
            <div className="lg:col-span-8">
              <div className="bg-white rounded-3xl shadow-2xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Form Fields</h3>
                
                  {/* All Fields in one sortable context */}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={fields.map(f => f.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="space-y-3">
                        {/* All Fields */}
                        {fields.map((field) => (
                          <SortableField
                            key={field.id}
                            field={field}
                            onEdit={handleEditField}
                            onDelete={handleDeleteField}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>

                  {/* No fields message */}
                  {fields.length === 0 && (
                    <div className="text-center py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 mt-4">
                      <p className="text-gray-500">No fields yet</p>
                      <p className="text-sm text-gray-400 mt-1">Click on a field type to add it</p>
                    </div>
                  )}

                  {/* Receipt Upload */}
                  <div className="mt-6">
                    <p className="text-sm text-gray-500 mb-3">System Upload Field</p>
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <Glyph icon={Paperclip} className="w-5 h-5 shrink-0 text-gray-600" />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1 min-w-0">
                            <h4 className="font-medium break-words min-w-0">
                              {getReceiptUploadLabel(form, 'Receipt Upload')}
                            </h4>
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700 shrink-0">
                              Required
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">File Upload • Field name: receipt</p>
                          <p className="text-xs text-amber-600 mt-1.5">If the campaign has “Requires receipt upload” disabled, this field will not be shown to users.</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={handleEditReceiptLabel}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit label"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <span className="text-xs bg-gray-200 px-3 py-1 rounded-full">System</span>
                        </div>
                      </div>
                    </div>
                  </div>
              </div>
            </div>

            {/* Add Fields - Right Side */}
            <div className="lg:col-span-4">
              <div className="bg-white rounded-3xl shadow-2xl p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Add Fields</h3>
                
                {/* System Fields Section */}
                {(() => {
                  const deletedSystemFields = JSON.parse(localStorage.getItem(`form_${id}_deleted_system_fields`) || '[]');
                  const systemFieldsDefinitions = [
                    { id: 'system_name', icon: User, label: 'Full Name', description: 'System field for name', field_type: 'text' },
                    { id: 'system_email', icon: Mail, label: 'Email Address', description: 'System field for email', field_type: 'email' },
                    { id: 'system_phone', icon: Phone, label: 'Phone Number', description: 'System field for phone', field_type: 'phone' }
                  ];
                  const availableSystemFields = systemFieldsDefinitions.filter(def => deletedSystemFields.includes(def.id));
                  
                  if (availableSystemFields.length > 0) {
                    return (
                      <>
                        <div className="mb-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                            <Glyph icon={Wrench} className="w-4 h-4 mr-2" />
                            System Fields
                          </h4>
                          <div className="space-y-2">
                            {availableSystemFields.map((systemField) => (
                              <button
                                key={systemField.id}
                                onClick={() => handleRestoreSystemField(systemField.id)}
                                className="w-full text-left p-3 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-xl transition-colors group"
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center">
                                    <Glyph icon={systemField.icon} className="w-5 h-5 mr-2 text-gray-700" />
                                    <div>
                                      <p className="font-medium text-gray-900 text-sm">{systemField.label}</p>
                                      <p className="text-xs text-gray-500">{systemField.description}</p>
                                    </div>
                                  </div>
                                  <svg className="w-4 h-4 text-amber-600 group-hover:text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                  </svg>
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="border-t pt-4">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Custom Field Types</h4>
                        </div>
                      </>
                    );
                  }
                  return null;
                })()}
                
                <div className="space-y-3">
                  {FIELD_TYPES.map((fieldType) => (
                    <button
                      key={fieldType.type}
                      onClick={() => handleAddField(fieldType)}
                      className="w-full text-left p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Glyph icon={fieldType.icon} className="w-6 h-6 mr-3 text-gray-700" />
                          <div>
                            <p className="font-medium text-gray-900">{fieldType.label}</p>
                            <p className="text-xs text-gray-500">{fieldType.description}</p>
                          </div>
                        </div>
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'settings' && (
          <div className="lg:col-span-12">
            <div className="bg-white rounded-3xl shadow-2xl p-8">
              <div className="max-w-2xl">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Form Settings</h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Form Name</label>
                  <input
                    type="text"
                    value={form?.name || ''}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    value={form?.description || ''}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                    rows={3}
                    placeholder="Brief description of this form"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Form headline (public page)</label>
                  <input
                    type="text"
                    value={form?.form_headline ?? ''}
                    onChange={(e) => setForm({ ...form, form_headline: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter to Win!"
                  />
                  <p className="mt-1 text-xs text-gray-500">Shown above the entry form on the campaign page. Leave blank for the default: Enter to Win!</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Submit Button Text</label>
                  <input
                    type="text"
                    value={form?.submit_button_text || 'Submit Entry'}
                    onChange={(e) => setForm({ ...form, submit_button_text: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Success Message</label>
                  <textarea
                    value={form?.success_message || 'Thank you for your submission!'}
                    onChange={(e) => setForm({ ...form, success_message: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                    rows={3}
                    placeholder="Message shown after successful submission"
                  />
                </div>
                
                <button
                  onClick={handleUpdateForm}
                  disabled={saving}
                  className="px-8 py-3 bg-teal-700 text-white rounded-xl hover:bg-teal-600 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'preview' && (
          <div className="lg:col-span-12">
            <div className="bg-white rounded-3xl shadow-2xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Form Preview</h3>
              
              <div className="max-w-2xl mx-auto">
                <form className="space-y-6">
                  {/* All Fields in Order */}
                  {fields.map((field) => {
                    if (field.is_system) {
                      // Render system fields
                      return (
                        <div key={field.id}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {field.field_label} {field.is_required && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type={field.field_type}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                            placeholder={field.field_type === 'email' ? 'your@email.com' : field.field_type === 'phone' ? '(123) 456-7890' : 'Enter your full name'}
                            disabled
                          />
                        </div>
                      );
                    }
                    
                    // Render custom fields
                    const fieldType = FIELD_TYPES.find(t => t.type === field.field_type);
                    
                    return (
                      <div key={field.id}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {field.field_label} {field.is_required && <span className="text-red-500">*</span>}
                        </label>
                        
                        {field.field_type === 'text' && (
                          <input
                            type="text"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                            placeholder={field.field_placeholder}
                            disabled
                          />
                        )}
                        
                        {field.field_type === 'email' && (
                          <input
                            type="email"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                            placeholder={field.field_placeholder}
                            disabled
                          />
                        )}
                        
                        {field.field_type === 'phone' && (
                          <input
                            type="tel"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                            placeholder={field.field_placeholder}
                            disabled
                          />
                        )}
                        
                        {field.field_type === 'date' && (
                          <input
                            type="date"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                            placeholder={field.field_placeholder}
                            disabled
                          />
                        )}
                        
                        {field.field_type === 'textarea' && (
                          <textarea
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl"
                            rows={4}
                            placeholder={field.field_placeholder}
                            disabled
                          />
                        )}

                        {field.field_type === 'video_recorder' && (
                          <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 bg-gray-50">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <p className="text-sm font-medium text-gray-900">Video Recorder</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Entrants can record a video and it will upload when they submit the form.
                                </p>
                              </div>
                              <span className="px-3 py-1 text-xs font-medium rounded-full bg-gray-200 text-gray-700">
                                Preview
                              </span>
                            </div>
                            <div className="mt-4 aspect-video rounded-lg bg-black/80 flex items-center justify-center text-white text-sm">
                              Camera preview appears here
                            </div>
                            <div className="mt-4 flex gap-3">
                              <button
                                type="button"
                                className="px-4 py-2 bg-red-500 text-white rounded-lg opacity-60 cursor-not-allowed"
                                disabled
                              >
                                Start Recording
                              </button>
                              <button
                                type="button"
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed"
                                disabled
                              >
                                Re-record
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {field.field_type === 'dropdown' && (
                          <select className="w-full px-4 py-3 border border-gray-300 rounded-xl" disabled>
                            <option value="">Choose an option</option>
                            {field.field_options?.options?.map((option, idx) => (
                              <option key={idx} value={option}>{option}</option>
                            ))}
                          </select>
                        )}
                        
                        {field.field_type === 'radio' && (
                          <div className="space-y-2">
                            {field.field_options?.options?.map((option, idx) => (
                              <label key={idx} className="flex items-center">
                                <input
                                  type="radio"
                                  name={field.field_name}
                                  className="mr-2"
                                  disabled
                                />
                                <span className="text-sm">{option}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        
                        {field.field_type === 'checkbox' && (
                          <div className="space-y-2">
                            {field.field_options?.options?.map((option, idx) => (
                              <label key={idx} className="flex items-center">
                                <input
                                  type="checkbox"
                                  className="mr-2"
                                  disabled
                                />
                                <span className="text-sm">{option}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Receipt Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {getReceiptUploadLabel(form)} <span className="text-red-500">*</span>
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p className="mt-2 text-sm text-gray-600">Click to upload or drag and drop</p>
                      <p className="text-xs text-gray-500">JPG, PNG, PDF up to 10MB</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="w-full py-3 bg-teal-700 text-white rounded-xl font-medium"
                    disabled
                  >
                    {form?.submit_button_text || 'Submit Entry'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Field Modal - Rendered via Portal */}
      {showFieldModal && fieldData && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              setShowFieldModal(false);
            }
          }}
        >
          <div
            className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="bg-white border-b px-6 py-4 flex justify-between items-center flex-shrink-0">
              <h3 className="text-lg font-bold text-gray-900">
                {isEditingSystemReceipt ? 'Edit Receipt Upload Label' : editingField ? 'Edit Field' : 'Add New Field'}
              </h3>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={() => setShowFieldModal(false)}
                className="text-gray-400 hover:text-gray-500 p-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
            <form onSubmit={(e) => e.preventDefault()} autoComplete="off" data-lpignore="true">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Field Type</label>
                {editingField ? (
                  isEditingSystemReceipt ? (
                    <div className="w-full px-4 py-3 border border-amber-300 bg-amber-50 rounded-xl text-sm text-amber-800 flex items-center justify-between">
                      <span className="flex items-center">
                        <Glyph icon={Paperclip} className="w-5 h-5 mr-2" />
                        <span className="font-medium">Receipt upload</span>
                      </span>
                      <span className="text-xs uppercase tracking-wide font-semibold text-amber-700">
                        Read only
                      </span>
                    </div>
                  ) : (
                  <div className="w-full px-4 py-3 border border-amber-300 bg-amber-50 rounded-xl text-sm text-amber-800 flex items-center justify-between">
                    <span className="flex items-center">
                      <Glyph
                        icon={editingField?.is_system && editingField.icon ? editingField.icon : (activeFieldType?.icon || Wrench)}
                        className="w-5 h-5 mr-2"
                      />
                      <span className="font-medium">
                        {activeFieldType?.label || fieldData.field_type}
                      </span>
                    </span>
                    <span className="text-xs uppercase tracking-wide font-semibold text-amber-700">
                      Read only
                    </span>
                  </div>
                  )
                ) : (
                  <select
                    value={fieldData.field_type}
                    onChange={(e) => setFieldData({ ...fieldData, field_type: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                  >
                    {FIELD_TYPES.map(type => (
                      <option key={type.type} value={type.type}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Field Label {isEditingSystemReceipt ? '' : '*'}
                </label>
                <input
                  ref={fieldLabelInputRef}
                  type="text"
                  name="field_label"
                  id="field_label_input"
                  value={fieldData?.field_label || ''}
                  onChange={(e) => {
                    if (fieldData) {
                      setFieldData({ ...fieldData, field_label: e.target.value });
                    }
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                  placeholder={isEditingSystemReceipt ? 'e.g., Proof of purchase (leave blank for default)' : 'e.g., Date of Birth'}
                  autoComplete="off"
                  data-lpignore="true"
                  data-form-type="other"
                  data-1p-ignore="true"
                  data-bwignore="true"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Field Name *</label>
                <input
                  type="text"
                  value={fieldData.field_name}
                  onChange={(e) => setFieldData({ ...fieldData, field_name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 ${fieldData.is_system ? 'bg-gray-100 text-gray-500 cursor-not-allowed focus:ring-0 focus:border-gray-300' : ''}`}
                  placeholder="e.g., date_of_birth"
                  readOnly={fieldData.is_system}
                  disabled={fieldData.is_system}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {fieldData.is_system
                    ? 'System field names are locked to ensure submissions stay compatible.'
                    : 'Use lowercase letters and underscores only'}
                </p>
              </div>
              
              {!isEditingSystemReceipt && !['textarea', 'video_recorder'].includes(fieldData.field_type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Placeholder</label>
                  <input
                    type="text"
                    value={fieldData.field_placeholder}
                    onChange={(e) => setFieldData({ ...fieldData, field_placeholder: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500"
                    placeholder="e.g., MM/DD/YYYY"
                  />
                </div>
              )}
              
              {FIELD_TYPES.find(t => t.type === fieldData.field_type)?.hasOptions && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
                  {fieldData.options.map((option, index) => (
                    <div key={index} className="flex space-x-2 mb-2">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...fieldData.options];
                          newOptions[index] = e.target.value;
                          setFieldData({ ...fieldData, options: newOptions });
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
                        placeholder={`Option ${index + 1}`}
                      />
                      {fieldData.options.length > 1 && (
                        <button
                          onClick={() => {
                            const newOptions = fieldData.options.filter((_, i) => i !== index);
                            setFieldData({ ...fieldData, options: newOptions });
                          }}
                          className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={() => setFieldData({ ...fieldData, options: [...fieldData.options, ''] })}
                    className="text-sm text-teal-600 hover:text-teal-700"
                  >
                    + Add Option
                  </button>
                </div>
              )}
              
              {/* Multiselect toggle for dropdown fields */}
              {fieldData.field_type === 'dropdown' && (
                <>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_multiselect"
                      checked={fieldData.is_multiselect}
                      onChange={(e) => setFieldData({ ...fieldData, is_multiselect: e.target.checked })}
                      className="h-4 w-4 text-teal-600 border-gray-300 rounded"
                    />
                    <label htmlFor="is_multiselect" className="ml-2 text-sm text-gray-700">
                      Allow multiple selections
                    </label>
                  </div>
                  
                  {/* Enable quantity input toggle (only show if multiselect is enabled) */}
                  {fieldData.is_multiselect && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="enable_quantity_input"
                        checked={fieldData.enable_quantity_input}
                        onChange={(e) => setFieldData({ ...fieldData, enable_quantity_input: e.target.checked })}
                        className="h-4 w-4 text-teal-600 border-gray-300 rounded"
                      />
                      <label htmlFor="enable_quantity_input" className="ml-2 text-sm text-gray-700">
                        Enable quantity input for each option
                      </label>
                    </div>
                  )}
                </>
              )}
              
              {!isEditingSystemReceipt && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_required"
                  checked={fieldData.is_required}
                  onChange={(e) => setFieldData({ ...fieldData, is_required: e.target.checked })}
                  className="h-4 w-4 text-teal-600 border-gray-300 rounded"
                />
                <label htmlFor="is_required" className="ml-2 text-sm text-gray-700">
                  This field is required
                </label>
              </div>
              )}
              
              {/* Conditional Visibility */}
              {!isEditingSystemReceipt && (
              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Conditional Visibility (Optional)
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Show or hide this field based on another field's value
                </p>
                
                {fieldData.field_conditions ? (
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Visibility Rule Active</span>
                      <button
                        onClick={() => setFieldData({ ...fieldData, field_conditions: null })}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Remove Rule
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Action</label>
                        <select
                          value={fieldData.field_conditions.visibility?.type || 'show_if'}
                          onChange={(e) => setFieldData({
                            ...fieldData,
                            field_conditions: {
                              visibility: {
                                ...fieldData.field_conditions.visibility,
                                type: e.target.value
                              }
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="show_if">Show if</option>
                          <option value="hide_if">Hide if</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Field</label>
                        <select
                          value={fieldData.field_conditions.visibility?.field || ''}
                          onChange={(e) => setFieldData({
                            ...fieldData,
                            field_conditions: {
                              visibility: {
                                ...fieldData.field_conditions.visibility,
                                field: e.target.value,
                                value: '' // Reset value when field changes
                              }
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="">Select field</option>
                          {fields
                            .filter(f => f.field_type === 'dropdown' && f.field_name !== fieldData.field_name)
                            .map(f => (
                              <option key={f.id} value={f.field_name}>
                                {f.field_label}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                    
                    {fieldData.field_conditions.visibility?.field && (
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Value</label>
                        <select
                          value={fieldData.field_conditions.visibility?.value || ''}
                          onChange={(e) => setFieldData({
                            ...fieldData,
                            field_conditions: {
                              visibility: {
                                ...fieldData.field_conditions.visibility,
                                value: e.target.value,
                                operator: 'equals' // Default operator
                              }
                            }
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          <option value="">Select value</option>
                          {fields
                            .find(f => f.field_name === fieldData.field_conditions.visibility.field)
                            ?.field_options?.options?.map(option => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                        </select>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => setFieldData({
                      ...fieldData,
                      field_conditions: {
                        visibility: {
                          type: 'show_if',
                          field: '',
                          operator: 'equals',
                          value: ''
                        }
                      }
                    })}
                    className="w-full p-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-teal-500 hover:text-teal-600 transition-colors"
                  >
                    + Add Visibility Rule
                  </button>
                )}
              </div>
              )}
            </div>
            </form>
            </div>

            <div className="bg-white border-t px-6 py-4 flex justify-end space-x-3 flex-shrink-0">
              <button
                onClick={() => setShowFieldModal(false)}
                className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveField}
                className="px-6 py-3 bg-teal-700 text-white rounded-lg hover:bg-teal-600 font-medium"
              >
                {isEditingSystemReceipt ? 'Save Label' : editingField ? 'Update Field' : 'Add Field'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </AdminLayout>
  );
}

export default FormEdit;
